import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import type { ReactElement } from 'react'

import { spacing } from '../KestrixDesignSystem.jsx'
import { KestrixPill } from '../KestrixPill.tsx'
import type { CellPill, CellValue } from './cell-display.types.ts'
import { isCellPillList } from './cell-display.types.ts'
import { PillOverflowChip } from './PillOverflowChip.tsx'
import { renderText } from './render-property-table-cell-content.tsx'
import { NEUTRAL_PILL_TONE } from './retrofit-subject-tone.ts'
import { PILL_SX, TonedTablePill } from './TonedTablePill.tsx'
import { useFittingPillCount } from './useFittingPillCount.ts'

const PILL_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: spacing[1],
  minWidth: 0,
  width: '100%',
  overflow: 'hidden'
}

const MEASURE_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: spacing[1],
  position: 'absolute',
  visibility: 'hidden',
  pointerEvents: 'none',
  left: 0,
  top: 0,
  width: 'max-content',
  whiteSpace: 'nowrap'
}

const OVERFLOW_PROTOTYPE_LABEL = '+99'

const visiblePillSlotSx = (
  truncateLeadingPill: boolean,
  fillBesideOverflow: boolean
): SxProps<Theme> => ({
  display: 'inline-flex',
  minWidth: 0,
  maxWidth: '100%',
  flex: fillBesideOverflow
    ? '1 1 0%'
    : truncateLeadingPill
      ? '0 1 auto'
      : '0 0 auto',
  overflow: truncateLeadingPill ? 'hidden' : 'visible'
})

const TonedPillList = ({
  pills
}: {
  readonly pills: readonly CellPill[]
}): ReactElement => {
  const { containerRef, measureRowRef, visibleCount } = useFittingPillCount(
    pills.length
  )
  const hiddenPills = pills.slice(visibleCount)
  const visiblePills = pills.slice(0, visibleCount)
  const truncateLeadingPill = visiblePills.length === 1
  const fillBesideOverflow = truncateLeadingPill && hiddenPills.length > 0

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', width: '100%', minWidth: 0 }}
    >
      <Box sx={PILL_ROW_SX} data-testid="property-table-cell-pill-row">
        {visiblePills.map((pill, index) => (
          <Box
            key={`${pill.label}-${index}`}
            component="span"
            sx={visiblePillSlotSx(truncateLeadingPill, fillBesideOverflow)}
          >
            <TonedTablePill
              pill={pill}
              truncated={truncateLeadingPill}
              dataTestId="property-table-cell-pill"
            />
          </Box>
        ))}
        {hiddenPills.length > 0 ? (
          <PillOverflowChip hiddenPills={hiddenPills} />
        ) : null}
      </Box>
      <Box ref={measureRowRef} aria-hidden sx={MEASURE_ROW_SX}>
        {pills.map((pill, index) => (
          <Box
            key={`measure-${pill.label}-${index}`}
            component="span"
            sx={{ display: 'inline-flex', flexShrink: 0 }}
          >
            <TonedTablePill pill={pill} />
          </Box>
        ))}
        <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
          <KestrixPill
            label={OVERFLOW_PROTOTYPE_LABEL}
            size="sm"
            selectedColor={NEUTRAL_PILL_TONE.fill}
            selectedBorder={NEUTRAL_PILL_TONE.border}
            sx={PILL_SX}
          />
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Shared renderer for `retrofitMeasurePills` and `subjectTypeBadges`.
 * Pills sit on one row. Extra pills collapse into a `+N` chip. A lone
 * pill, or the first pill beside `+N`, truncates with an ellipsis when the
 * column is too narrow. Hovering `+N` opens a popover of the hidden pills.
 */
export const renderTonedPillList = (value: CellValue): ReactElement => {
  if (!isCellPillList(value)) {
    return renderText(value)
  }
  if (value.pills.length === 0) {
    return renderText(null)
  }
  return <TonedPillList pills={value.pills} />
}
